import { z } from "zod";
import i18n from "@/i18n";
import { apiService } from "../api.service";
import { type AuditLogDisplay, AuditLogDisplaySchema } from "../job/job.types";

const AuditLogArray = z.array(AuditLogDisplaySchema);

import { type PlatformRole, PlatformRoleSchema } from "../auth/auth.types";
import type { ServiceResult } from "../shared/shared-types";
import { UserApiError } from "./user.errors";
import {
	type CreatePlatformUserInfo,
	CreatePlatformUserInfoSchema,
	type GetUserByIdResponse,
	type GetUserCountParams,
	type GetUserCountResponse,
	type GetUserPermissionsResponse,
	type GetUserStatusesResponse,
	type GetUsersParams,
	type GetUsersResponse,
	type ResetPasswordRequest,
	ResetPasswordRequestSchema,
	type UpdateUserInfo,
	UpdateUserInfoSchema,
	type UpdateUserPlatformRole,
	UpdateUserPlatformRoleSchema,
	type UpdateUserStatus,
	UpdateUserStatusSchema,
	type UserMfaInfo,
	UserMfaInfoSchema,
	UserSchema,
	UserStatusesArraySchema,
	type ValidatePasswordResult,
} from "./user.types";

/** Translation keys for password validation API error codes (only these are shown to the user) */
const PASSWORD_VALIDATION_KEYS = {
	"password_rejected.too_short": "setPassword.validation.tooShort",
	"password_rejected.pwned": "setPassword.validation.pwned",
} as const satisfies Record<string, string>;

/** Only show these validation errors in the UI; all other codes are omitted */
const PASSWORD_ERROR_CODES_TO_SHOW = new Set(
	Object.keys(PASSWORD_VALIDATION_KEYS),
);

/** Get response body from either Axios error (response.data) or transformed ApiError (.data) */
function getPasswordErrorResponseData(
	error: unknown,
): Record<string, unknown> | undefined {
	if (!error || typeof error !== "object") return undefined;
	const err = error as Record<string, unknown>;
	// Transformed ApiError from api.service interceptor stores response body in .data
	if (err.data && typeof err.data === "object")
		return err.data as Record<string, unknown>;
	// Raw Axios error
	const response = err.response as { data?: unknown } | undefined;
	const data = response?.data;
	if (!data || typeof data !== "object") return undefined;
	return data as Record<string, unknown>;
}

/**
 * User Service
 *
 * Handles all user-related API operations including:
 * - Fetching user details
 * - Pagination and counting
 * - Updating user information
 * - Managing user permissions
 * - Status management
 */
export class UserService {
	private readonly baseUrl = "/users";

	/**
	 * Get a user by their ID
	 * GET /users/{userId}
	 *
	 * @param userId - The user's UUID
	 * @returns User object with full details
	 * @throws Error if user not found or API error
	 */
	async getUserById(userId: string): Promise<GetUserByIdResponse> {
		const data = await apiService.get<GetUserByIdResponse>(
			`${this.baseUrl}/${userId}`,
		);
		console.log("getUserById: ", data);
		try {
			const parsedData = UserSchema.parse(data);
			return parsedData;
		} catch (error) {
			console.error("❌ UserSchema validation error:", error);
			if (error instanceof z.ZodError) {
				console.error(
					"Validation errors:",
					JSON.stringify(error.issues, null, 2),
				);
			}
			throw error;
		}
	}

	/**
	 * Get paginated users
	 * GET /users/{start}/{count}
	 *
	 * @param start - Starting index (0-based)
	 * @param count - Number of users to return
	 * @param params - Optional query parameters:
	 *   - UserId: Filter by specific user ID
	 *   - SearchTerm: Search by name or email
	 *   - Status: Filter by user status code
	 *   - HasPlatformRoles: Include only users with platform roles
	 *   - HasOrganizationUsers: Include only users with organization memberships
	 *   - OrderBy: Sort results (Fields, Descending)
	 * @returns Array of users
	 * @throws Error if pagination parameters invalid or API error
	 */
	async getUsers(
		start: number,
		count: number,
		params?: GetUsersParams,
	): Promise<GetUsersResponse> {
		const data = await apiService.get<GetUsersResponse>(
			`${this.baseUrl}/${start}/${count}`,
			{ params },
		);
		return data;
	}

	/**
	 * Get total count of users, optionally filtered
	 * GET /users/count
	 *
	 * @param params - Optional filter parameters:
	 *   - UserId: Filter by specific user ID
	 *   - SearchTerm: Search by name or email
	 *   - Status: Filter by user status code
	 *   - HasPlatformRoles: Include only users with platform roles
	 *   - HasOrganizationUsers: Include only users with organization memberships
	 *   - OrderBy: Sort results (Fields, Descending)
	 * @returns Total number of users matching the filter criteria
	 * @throws Error if API error
	 */
	async getUserCount(
		params?: GetUserCountParams,
	): Promise<GetUserCountResponse> {
		const data = await apiService.get<GetUserCountResponse>(
			`${this.baseUrl}/count`,
			{ params },
		);

		if (typeof data !== "number") {
			throw new Error("Invalid user count response");
		}

		return data;
	}

	/**
	 * Get a user's permissions
	 * GET /users/{userId}/permissions
	 *
	 * @param userId - The user's UUID
	 * @returns User object with permissions included
	 * @throws Error if user not found or API error
	 */
	async getUserPermissions(
		userId: string,
	): Promise<GetUserPermissionsResponse> {
		const data = await apiService.get<GetUserPermissionsResponse>(
			`${this.baseUrl}/${userId}/permissions`,
		);
		return UserSchema.parse(data);
	}

	/**
	 * Update a user's information
	 * PUT /users/{userId}
	 *
	 * @param userId - The user's UUID
	 * @param updateData - Updated user information (firstName, lastName, displayName)
	 * @returns void on success
	 * @throws Error if validation fails or API error
	 */
	async updateUserInfo(
		userId: string,
		updateData: UpdateUserInfo,
	): Promise<void> {
		const validatedData = UpdateUserInfoSchema.parse(updateData);
		await apiService.put(`${this.baseUrl}/${userId}`, validatedData);
	}

	/**
	 * Update a user's status
	 * PUT /users/{userId}/status
	 *
	 * @param userId - The user's UUID
	 * @param statusData - Status update data (required status string)
	 * @returns void on success
	 * @throws Error if validation fails or API error
	 */
	async updateUserStatus(
		userId: string,
		statusData: UpdateUserStatus,
	): Promise<void> {
		const validatedData = UpdateUserStatusSchema.parse(statusData);
		await apiService.put(`${this.baseUrl}/${userId}/status`, validatedData);
	}

	/**
	 * Update a user's platform role
	 * PUT /users/{userId}/platform-role
	 *
	 * @param userId - The user's UUID
	 * @param roleData - Platform role update data (required platformRoleCode string)
	 * @returns void on success
	 * @throws Error if validation fails or API error
	 */
	async updateUserPlatformRole(
		userId: string,
		roleData: UpdateUserPlatformRole,
	): Promise<void> {
		const validatedData = UpdateUserPlatformRoleSchema.parse(roleData);
		await apiService.put(
			`${this.baseUrl}/${userId}/platform-role`,
			validatedData,
		);
	}

	// ========================================================================
	// CREATE + ROLES FETCH
	// ========================================================================

	async createPlatformUser(
		data: CreatePlatformUserInfo,
	): Promise<ServiceResult> {
		try {
			// Validate input (schema coerces roleId to number)
			const validated = CreatePlatformUserInfoSchema.parse(data);

			// Call API
			const res = await apiService.post("/users", {
				firstName: validated.firstName,
				lastName: validated.lastName,
				email: validated.email,
				roleId: validated.roleId,
				// Added per swagger2.json diff - phone property for POST /users
				phone: validated.phone ?? null,
			});

			// Normalize API response to ServiceResult (avoid strict User parse)
			const r = res as unknown as {
				id?: string;
				isSuccess?: boolean;
				success?: boolean;
				affectedId?: string | null;
				errors?: string[];
				errorMessages?: string[] | null;
			};

			if (r && typeof r === "object") {
				if (typeof r.id === "string" && r.id.length > 0) {
					return { isSuccess: true, errorMessages: null, affectedId: r.id };
				}
				if (
					r.affectedId !== undefined ||
					r.isSuccess !== undefined ||
					r.success !== undefined
				) {
					return {
						isSuccess: Boolean(r.isSuccess ?? r.success ?? true),
						errorMessages: r.errorMessages ?? r.errors ?? null,
						affectedId: (r.affectedId as string | null) ?? null,
					};
				}
			}

			// Fallback: treat as success without id
			return { isSuccess: true, errorMessages: null, affectedId: null };
		} catch (error) {
			if (error instanceof UserApiError) throw error;
			if (error instanceof z.ZodError) {
				const msg = error.issues
					.map((i) => `${i.path.join(".")}: ${i.message}`)
					.join(", ");
				throw new UserApiError(`Validation failed: ${msg}`, error);
			}
			throw new UserApiError(
				`Failed to create user: ${error instanceof Error ? error.message : "Unknown error"}`,
				error,
			);
		}
	}

	async getPlatformRoles(): Promise<PlatformRole[]> {
		const response = await apiService.get<PlatformRole[]>(`/platform-roles`);
		const PlatformRolesArray = z.array(PlatformRoleSchema);
		return PlatformRolesArray.parse(response);
	}

	/**
	 * Get all user statuses
	 * GET /users/statuses
	 *
	 * @returns Array of user statuses
	 * @throws Error if API error
	 */
	async getUserStatuses(
		forPlatformLevelUsers = false,
	): Promise<GetUserStatusesResponse> {
		const response = await apiService.get<GetUserStatusesResponse>(
			`${this.baseUrl}/statuses`,
			{ params: { forPlatformLevelUsers } },
		);
		return UserStatusesArraySchema.parse(response);
	}

	/**
	 * Export users to CSV
	 * GET /users/{start}/{count}/export
	 *
	 * @param start - Starting index (1-based)
	 * @param count - Number of users to export
	 * @param params - Optional query parameters (same as getUsers)
	 * @returns Blob containing CSV data
	 * @throws Error if export fails
	 */
	async exportUsers(
		start: number,
		count: number,
		params?: GetUsersParams,
	): Promise<Blob> {
		const response = await apiService.get<Blob>(
			`${this.baseUrl}/${start}/${count}/export`,
			{
				params,
				responseType: "blob",
				headers: {
					Accept: "text/csv",
				},
			},
		);
		return response;
	}

	// ========================================================================
	// PASSWORD VALIDATION
	// ========================================================================

	/**
	 * Validate a user's password strength against server-side rules
	 * GET /users/{userId}/validate-password?password=...
	 *
	 * Added per swagger2.json diff - new endpoint for password validation
	 *
	 * @param userId - The user's UUID
	 * @param password - The password to validate
	 * @returns Validation outcome, user-facing `error`, and raw `errors` codes for UI checklists
	 */
	async validatePassword(
		userId: string,
		password: string,
	): Promise<ValidatePasswordResult> {
		const formatPasswordErrors = (codes: string[]): string => {
			const messages = codes
				.filter((code) => PASSWORD_ERROR_CODES_TO_SHOW.has(code))
				.map((code) =>
					i18n.t(
						PASSWORD_VALIDATION_KEYS[
							code as keyof typeof PASSWORD_VALIDATION_KEYS
						],
					),
				)
				.filter(Boolean);
			return messages.length > 0 ? messages.join(" ") : "";
		};

		try {
			const data = await apiService.get<{
				success?: boolean;
				errors?: string[];
			}>(`${this.baseUrl}/${userId}/validate-password`, {
				params: { password },
			});
			if (data?.success === false) {
				const codes = Array.isArray(data.errors) ? data.errors : [];
				return {
					isValid: false,
					error: formatPasswordErrors(codes),
					errors: codes,
				};
			}
			return { isValid: true, errors: [] };
		} catch (error: unknown) {
			const responseData = getPasswordErrorResponseData(error);
			const errors = Array.isArray(responseData?.errors)
				? (responseData.errors as string[])
				: [];
			const rawMessage = errors?.length
				? null
				: (typeof responseData?.detail === "string"
						? responseData.detail
						: undefined) ||
					(typeof responseData?.title === "string"
						? responseData.title
						: undefined) ||
					(error instanceof Error ? error.message : undefined);
			const errorMessage = errors?.length
				? formatPasswordErrors(errors)
				: rawMessage &&
						!String(rawMessage)
							.toLowerCase()
							.includes("request failed with status code")
					? rawMessage
					: "";
			return { isValid: false, error: errorMessage, errors };
		}
	}

	// ========================================================================
	// PASSWORD RESET OPERATIONS
	// ========================================================================

	/**
	 * Request a password reset for a user
	 * PUT /users/{userId}/request-password-reset
	 *
	 * This triggers the password reset flow, typically sending an email
	 * to the user with a reset link/token.
	 *
	 * @param userId - The user's UUID
	 * @returns void on success
	 * @throws Error if user not found or API error
	 */
	async requestPasswordReset(userId: string): Promise<void> {
		await apiService.put(`${this.baseUrl}/${userId}/request-password-reset`);
	}

	/**
	 * Force a password reset for a user (admin action)
	 * PUT /users/{userId}/force-password-reset
	 *
	 * This forces the user to reset their password on next login.
	 * Typically used by administrators to require a password change.
	 *
	 * @param userId - The user's UUID
	 * @returns void on success
	 * @throws Error if user not found or API error
	 */
	async forcePasswordReset(userId: string): Promise<void> {
		await apiService.put(`${this.baseUrl}/${userId}/force-password-reset`);
	}

	/**
	 * Reset a user's password with a token
	 * PUT /users/{userId}/reset-password
	 *
	 * This completes the password reset flow using the token
	 * received via email after requesting a password reset.
	 *
	 * @param userId - The user's UUID
	 * @param resetData - Object containing resetPasswordToken and newPassword
	 * @returns void on success
	 * @throws Error if token invalid, expired, or API error
	 */
	async resetPassword(
		userId: string,
		resetData: ResetPasswordRequest,
	): Promise<void> {
		const validatedData = ResetPasswordRequestSchema.parse(resetData);
		await apiService.put(
			`${this.baseUrl}/${userId}/reset-password`,
			validatedData,
		);
	}

	// ========================================================================
	// MFA OPERATIONS
	// ========================================================================

	/**
	 * Send MFA verification code to a phone number
	 * POST /users/{userId}/mfa/verification-code
	 *
	 * Request body is a JSON string (phone number). Triggers sending an SMS
	 * verification code to the given number.
	 *
	 * @param userId - The user's UUID
	 * @param phoneNumber - Phone number (digits only, e.g. 10-digit US)
	 * @returns void on success
	 * @throws Error if user not found, invalid request, or API error
	 */
	async sendMfaVerificationCode(
		userId: string,
		phoneNumber: string,
	): Promise<void> {
		// Request body is JSON string per swagger2.json
		await apiService.post(
			`${this.baseUrl}/${userId}/mfa/verification-code`,
			JSON.stringify(phoneNumber),
			{
				headers: {
					"Content-Type": "application/json",
				},
			},
		);
	}

	/**
	 * Update user MFA (submit phone + verification code to enable/verify)
	 * PUT /users/{userId}/mfa
	 *
	 * @param userId - The user's UUID
	 * @param data - Phone number and verification code from SMS
	 * @returns void on success
	 * @throws Error if invalid code, user not found, or API error
	 */
	async updateMfa(userId: string, data: UserMfaInfo): Promise<void> {
		const validated = UserMfaInfoSchema.parse(data);
		await apiService.put(`${this.baseUrl}/${userId}/mfa`, validated);
	}

	// ========================================================================
	// AVATAR OPERATIONS
	// ========================================================================

	/**
	 * Get a user's avatar image as a Blob
	 * GET /users/{userId}/avatar
	 *
	 * @param userId - The user's UUID
	 * @returns Blob containing the avatar image (JPEG)
	 * @throws Error if avatar not found or API error
	 */
	async getUserAvatar(userId: string): Promise<Blob> {
		const response = await apiService.get<Blob>(
			`${this.baseUrl}/${userId}/avatar`,
			{
				responseType: "blob",
				headers: {
					Accept: "image/jpeg",
				},
			},
		);
		return response;
	}

	/**
	 * Upload a new avatar image for a user
	 * POST /users/{userId}/avatar
	 *
	 * @param userId - The user's UUID
	 * @param file - The image file to upload (JPEG/PNG)
	 * @returns void on success
	 * @throws Error if upload fails or API error
	 */
	async uploadUserAvatar(userId: string, file: File | Blob): Promise<void> {
		const formData = new FormData();
		// Use 'file' as the field name per swagger2.json specification
		formData.append("file", file);

		await apiService.post(`${this.baseUrl}/${userId}/avatar`, formData, {
			headers: {
				"Content-Type": "multipart/form-data",
			},
		});
	}

	/**
	 * Delete a user's avatar
	 * DELETE /users/{userId}/avatar
	 *
	 * @param userId - The user's UUID
	 * @returns void on success
	 * @throws Error if API error
	 */
	async deleteUserAvatar(userId: string): Promise<void> {
		await apiService.delete(`${this.baseUrl}/${userId}/avatar`);
	}

	/**
	 * Delete the signed-in user's own account.
	 * DELETE /users/{userId}
	 *
	 * Skips the global 401 login redirect so the caller can open
	 * /login?reason=access-removed when the account is already gone.
	 */
	async deleteUser(userId: string): Promise<void> {
		await apiService.delete(`${this.baseUrl}/${userId}`, {
			skipUnauthorizedRedirect: true,
		});
	}

	async getUserHistoryCount(userId: string): Promise<number> {
		const response = await apiService.get<number>(
			`${this.baseUrl}/${encodeURIComponent(userId)}/history/count`,
		);
		return z.number().parse(response);
	}

	async getUserHistory(
		userId: string,
		start: number,
		count: number,
	): Promise<AuditLogDisplay[]> {
		const response = await apiService.get<AuditLogDisplay[]>(
			`${this.baseUrl}/${encodeURIComponent(userId)}/history/${start}/${count}`,
		);
		return AuditLogArray.parse(response);
	}
}

/**
 * Singleton instance of UserService
 * Use this exported instance throughout the application
 */
export const userService = new UserService();
